import React, { useEffect } from "react";
import { useVoiceWidget, VoiceWidgetConfig } from "../hooks/useVoiceWidget";
import {
  injectPulseAnimations,
  getAnimationForState,
  BADGE_COLORS,
} from "../utils/pulseAnimations";

interface AvatarFloatingButtonProps extends VoiceWidgetConfig {
  avatarUrl: string;
  name: string;
  position?: "bottom-left" | "bottom-right" | "top-left" | "top-right";
  size?: number;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

const AvatarFloatingButton: React.FC<AvatarFloatingButtonProps> = ({
  avatarUrl,
  name,
  // position kept for API/compat; viewport placement is owned by RemstalVoiceWidget shell
  position: _position = "bottom-right",
  size = 96, // Reasonably larger default size (was 56, now 96)
  onClick,
  disabled = false,
  className = "",
  ...widgetConfig
}) => {
  const {
    buttonState,
    isWidgetActive,
    isWidgetDisabled,
    handleButtonClick,
    error,
  } = useVoiceWidget(widgetConfig);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      handleButtonClick();
    }
  };

  const isDisabled = disabled || isWidgetDisabled || buttonState === "disabled";

  // Inject pulse animation styles
  useEffect(() => {
    injectPulseAnimations("avatar-floating-button-styles");
  }, []);

  const isRecording = buttonState === "recording";

  // Relative in-shell: parent .remstal-voice-widget owns position:fixed + corner insets.
  const getButtonStyles = (): React.CSSProperties => {
    return {
      position: "relative",
      display: "block",
      width: size,
      height: size,
      borderRadius: "50%",
      overflow: "hidden",
      // Glass launcher: translucent bg + backdrop blur + crisp bright hairline rim
      background: "rgba(255, 255, 255, 0.22)",
      backdropFilter: "blur(22px) saturate(140%)",
      WebkitBackdropFilter: "blur(22px) saturate(140%)",
      border: "1px solid rgba(255, 255, 255, 0.88)",
      boxShadow: isRecording
        ? "0 14px 35px rgba(6, 10, 18, 0.28), inset 0 1px 0 rgba(255,255,255,0.60)"
        : "0 14px 35px rgba(6, 10, 18, 0.22), inset 0 1px 0 rgba(255,255,255,0.60)",
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? "not-allowed" : "pointer",
      padding: 0,
      animation: getAnimationForState(buttonState as any, isWidgetActive),
    };
  };

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={className}
      style={getButtonStyles()}
      title={isDisabled ? "Ein anderer Assistent ist gerade aktiv" : name}
      data-voice-active={isRecording ? "true" : undefined}
    >
      <img
        src={avatarUrl}
        alt={name}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "50%",
          display: "block",
        }}
      />

      {/* Glass pulse / activity rings rendered only while recording. */}
      {isRecording && (
        <>
          {/* Ambient glow halo behind the rings. */}
          <div
            style={{
              position: "absolute",
              inset: "-14px",
              borderRadius: "50%",
              background: "var(--heyai-pulse-gradient, linear-gradient(135deg, rgba(139,92,246,0.38), rgba(119,175,255,0.52)))",
              animation: "heyaiGlow 2.4s ease-in-out infinite",
              pointerEvents: "none",
              zIndex: -1,
            }}
          />
          {/* Inner ring: primary pulse. */}
          <div
            data-testid="voice-pulse"
            data-voice-active="true"
            style={{
              position: "absolute",
              inset: "-8px",
              borderRadius: "50%",
              border: "1.5px solid rgba(119,175,255,0.65)",
              animation: "heyaiPulse 1.8s ease-out infinite",
              pointerEvents: "none",
            }}
          />
          {/* Outer ring: secondary pulse, staggered. */}
          <div
            style={{
              position: "absolute",
              inset: "-8px",
              borderRadius: "50%",
              border: "1px solid rgba(139,92,246,0.42)",
              animation: "heyaiPulse2 1.8s ease-out infinite 0.6s",
              pointerEvents: "none",
            }}
          />
        </>
      )}

      {/* Status indicators */}
      {isWidgetActive && !isRecording && (
        <div
          style={{
            position: "absolute",
            top: "-4px",
            right: "-4px",
            width: "12px",
            height: "12px",
            backgroundColor: "var(--heyai-accent, #77AFFF)",
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.92)",
          }}
        />
      )}

      {isWidgetDisabled && (
        <div
          style={{
            position: "absolute",
            top: "-4px",
            right: "-4px",
            width: "12px",
            height: "12px",
            backgroundColor: BADGE_COLORS.disabled,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.92)",
          }}
        />
      )}

      {/* Connecting indicator */}
      {buttonState === "connecting" && (
        <div
          style={{
            position: "absolute",
            bottom: "-4px",
            right: "-4px",
            width: "12px",
            height: "12px",
            backgroundColor: BADGE_COLORS.connecting,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.92)",
            animation: "avatar-pulse-spin 1s linear infinite",
          }}
        />
      )}
    </button>
  );
};

export default AvatarFloatingButton;
